import React, { useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { FiKey, FiCheckCircle, FiAlertCircle, FiShield } from 'react-icons/fi';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface PasskeyRegistrationProps {
  userId?: string;
  userEmail: string;
  onSuccess?: () => void;
  onSkip?: () => void;
  isNewUser?: boolean;
}

const PasskeyRegistration: React.FC<PasskeyRegistrationProps> = ({
  userEmail,
  onSuccess,
  onSkip,
  isNewUser = false
}) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [nickname, setNickname] = useState('');
  const [showNicknameInput, setShowNicknameInput] = useState(false);

  const detectAuthenticatorType = (): string => {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();
    
    if (userAgent.includes('mac') || platform.includes('mac')) {
      return 'Touch ID';
    } else if (userAgent.includes('windows')) {
      return 'Windows Hello';
    } else if (userAgent.includes('android')) {
      return 'Android Biometric';
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      return 'Face ID / Touch ID';
    }
    return 'Security Key';
  };

  const handleRegisterPasskey = async () => {
    if (!window.PublicKeyCredential) {
      toast.error('Your browser doesn\'t support passkeys. Please use a modern browser.');
      return;
    }

    setIsRegistering(true);
    
    try {
      // Get registration options from server
      const optionsResponse = await api.getWebAuthnRegistrationOptions({
        email: userEmail,
        username: userEmail.split('@')[0]
      });

      const options = optionsResponse.data;

      // Start registration with browser
      let attResp;
      try {
        attResp = await startRegistration({ optionsJSON: options });
      } catch (error: any) {
        if (error.name === 'NotAllowedError') {
          toast.error('Registration was cancelled or timed out. Please try again.');
        } else {
          toast.error('Failed to register passkey. Please try again.');
        }
        setIsRegistering(false);
        return;
      }

      // Verify registration with server
      const verificationResponse = await api.verifyWebAuthnRegistration({
        ...attResp,
        nickname: nickname || detectAuthenticatorType(),
        challenge: options.challenge
      });

      if (verificationResponse.data.verified) {
        toast.success('Passkey registered successfully!');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error('Failed to verify passkey. Please try again.');
      }
    } catch (error: any) {
      console.error('Passkey registration error:', error);
      toast.error(error.response?.data?.error || 'Failed to register passkey');
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
          <FiKey className="w-6 h-6 text-primary-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          {isNewUser ? 'Set Up Your Passkey' : 'Add a Passkey'}
        </h3>
        <p className="text-sm text-gray-600 mt-2">
          {isNewUser 
            ? 'Secure your account with a passkey for passwordless sign-in'
            : 'Add a passkey for faster, more secure sign-ins'
          }
        </p>
      </div>

      {/* Benefits */}
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="space-y-2">
          <div className="flex items-start">
            <FiCheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-gray-700">
              Works with Face ID, Touch ID, Windows Hello, and YubiKeys
            </span>
          </div>
          <div className="flex items-start">
            <FiCheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-gray-700">
              Compatible with 1Password, Proton Pass, and other password managers
            </span>
          </div>
          <div className="flex items-start">
            <FiCheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-gray-700">
              No passwords to remember or type
            </span>
          </div>
        </div>
      </div>

      {/* Nickname Input (Optional) */}
      {showNicknameInput && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passkey Nickname (optional)
          </label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder={`e.g., ${detectAuthenticatorType()}, YubiKey, Work Laptop`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">
            Help identify this passkey in your account settings
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleRegisterPasskey}
          disabled={isRegistering}
          className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isRegistering ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Setting up passkey...
            </>
          ) : (
            <>
              <FiShield className="w-5 h-5 mr-2" />
              {isNewUser ? 'Create Passkey' : 'Add Passkey'}
            </>
          )}
        </button>

        {!showNicknameInput && (
          <button
            type="button"
            onClick={() => setShowNicknameInput(true)}
            className="w-full text-sm text-gray-600 hover:text-gray-700"
          >
            Add a nickname to this passkey
          </button>
        )}

        {onSkip && isNewUser && (
          <button
            type="button"
            onClick={onSkip}
            className="w-full py-2 px-4 text-gray-600 hover:text-gray-700"
          >
            Skip for now
          </button>
        )}
      </div>

      {/* Security Note */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-start">
          <FiAlertCircle className="w-5 h-5 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-600">
            <p className="font-medium mb-1">Your passkey is stored securely:</p>
            <ul className="space-y-0.5">
              <li>• On your device (Face ID, Touch ID, etc.)</li>
              <li>• In your password manager (1Password, etc.)</li>
              <li>• On your security key (YubiKey, etc.)</li>
            </ul>
            <p className="mt-2">We never see or store your biometric data.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasskeyRegistration;